import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Icon from "../components/Icon";
import { useData } from "../data/DataProvider";
import * as sp from "../services/sqlApi";
import { ADMIN_EMAILS, FINANCE_EMAILS } from "../services/authConfig";
import { roleFamily, BUILDING_TYPES, positionBucket, POSITION_ORDER, isOpenSeat, OPEN_SEAT } from "../services/roles";
import { roleColor } from "../data/roleColors";
import MoveJobWizard from "../components/MoveJobWizard";
import "../styles.css";

// All dropdown option lists are alphabetical.
const alpha = (a, b) => a.localeCompare(b);
const COMPANY_ROLES = ["Sr. Supt", "Supt", "Finish Supt", "Asst. Supt", "Gen. Supt", "Sr. PM", "PM", "APM", "PE", "FE", "QA/QC Engineer", "PX", "Precon", "Director", "Accountant", "Admin", "Other"].sort(alpha);
const PROJECT_ROLES = ["Sr. Supt", "Supt", "Finish Supt", "Asst. Supt", "Gen. Supt", "Sr. PM", "PM", "APM", "QA/QC Engineer", "PE", "FE", "PX", "Project Accountant", "Estimator", "Precon", "Director", "Admin", "Staff", "Other"].sort(alpha);
const RATE_ROLES = ["PX", "Project Accountant", "Sr. PM", "PM", "APM", "Sr. Supt", "Supt", "Finish Supt", "Asst. Supt", "Gen. Supt", "QA/QC Engineer", "PE", "FE", "Intern", "Estimator"].sort(alpha);

// Hardaway 2026 billable rates (PX + Project Accountant monthly; rest weekly).
const DEFAULT_RATES_2026 = [
  { Role: "PX", Type: "Monthly", Rate: 7800 },
  { Role: "Project Accountant", Type: "Monthly", Rate: 1900 },
  { Role: "Sr. PM", Type: "Weekly", Rate: 5600 },
  { Role: "PM", Type: "Weekly", Rate: 4600 },
  { Role: "APM", Type: "Weekly", Rate: 3200 },
  { Role: "PE", Type: "Weekly", Rate: 3125 },
  { Role: "Intern", Type: "Weekly", Rate: 1325 },
  { Role: "Sr. Supt", Type: "Weekly", Rate: 5600 },
  { Role: "Supt", Type: "Weekly", Rate: 4600 },
  { Role: "Finish Supt", Type: "Weekly", Rate: 3800 },
  { Role: "Asst. Supt", Type: "Weekly", Rate: 3200 },
  { Role: "FE", Type: "Weekly", Rate: 3125 },
  { Role: "Estimator", Type: "Weekly", Rate: 4600 },
  { Role: "QA/QC Engineer", Type: "Weekly", Rate: 2300 },
];
const PROJECT_STATUSES = ["Active", "Potential", "Completed", "On Hold"];

const ARCHETYPES = [
  "Daredevil", "Enterpriser", "Philosopher", "Trailblazer", "Architect",
  "Technical Expert", "Scholar", "Specialist", "Craftsman",
  "Persuader", "Rainmaker", "Influencer", "Debater", "Socializer",
  "Administrator", "Coordinator", "Facilitator", "Operator", "Traditionalist"
].sort(alpha);

// Admin console sections — icon + one-line description so you can see what
// lives where without clicking through.
const ADMIN_TABS = [
  { id: "teams", label: "Manage Teams", icon: "users", desc: "Staff jobs, open seats & support" },
  { id: "projects", label: "Projects", icon: "building", desc: "Create, edit & delete jobs" },
  { id: "rates", label: "Rates", icon: "dollar", desc: "Billable rates by role" },
  { id: "people", label: "People", icon: "user", desc: "Roles, scores, admin & active" },
  { id: "import", label: "Add Person", icon: "plus", desc: "New Culture Index profile" },
  { id: "feedback", label: "Feedback", icon: "messages", desc: "Requests & bugs from the team" },
];

const EMPTY_PERSON = {
  FullName: "", Email: "", JobTitle: "", CompanyRole: "PM", Archetype: "",
  Survey_A: "", Survey_B: "", Survey_C: "", Survey_D: "", Survey_EU: "", Survey_L: "", Survey_I: "",
  Job_A: "", Job_B: "", Job_C: "", Job_D: "", Job_EU: "", Job_L: "", Job_I: "",
};
const EMPTY_PROJECT = { Title: "", JobName: "", Address: "", ProjectExecutive: "", ProjectAccountant: "", Status: "Active", BuildingType: "Commercial", Confidence: 50, StartDate: "", CompletionDate: "", GCBudget: "", GCRemaining: "", ProcoreProjectId: "" };

const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
const dateOnly = (d) => (d ? String(d).slice(0, 10) : "");

function TraitInput({ label, surveyKey, jobKey, values, onChange }) {
  return (
    <div style={{ marginBottom: "12px" }}>
      <div style={{ fontFamily: "var(--font-mono)", fontSize: "12px", color: "var(--accent)", marginBottom: "6px", fontWeight: 600 }}>{label}</div>
      <div className="flex gap-8">
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: "10px", color: "var(--text-muted)", marginBottom: "4px", textTransform: "uppercase" }}>Survey</div>
          <input type="number" min="0" max="10" className="form-input" style={{ padding: "8px 10px" }} placeholder="0–10"
            value={values[surveyKey]} onChange={e => onChange(surveyKey, e.target.value)} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: "10px", color: "var(--text-muted)", marginBottom: "4px", textTransform: "uppercase" }}>Job</div>
          <input type="number" min="0" max="10" className="form-input" style={{ padding: "8px 10px" }} placeholder="0–10"
            value={values[jobKey]} onChange={e => onChange(jobKey, e.target.value)} />
        </div>
      </div>
    </div>
  );
}

export default function AdminImport() {
  const { people, projects, teamMembers, billableRates, addPerson, editPerson, removePerson, addProject, editProject, removeProject, moveJobAndCrew, addTeamMember, editTeamMember, removeTeamMember, addRate, editRate, removeRate, reload, getToken, getDeletedProjects, restoreProject } = useData();
  const [tab, setTab] = useState("teams");

  // rates
  const EMPTY_RATE = { Role: "", Type: "Weekly", Rate: "" };
  const [rateForm, setRateForm] = useState(EMPTY_RATE);
  const [rateEditId, setRateEditId] = useState(null);
  const [rateMsg, setRateMsg] = useState("");
  const [rateBusy, setRateBusy] = useState(false);
  const handleRate = (k, v) => setRateForm(f => ({ ...f, [k]: v }));
  function loadRate(r) {
    if (!r) { setRateForm(EMPTY_RATE); setRateEditId(null); return; }
    setRateEditId(r.Id);
    setRateForm({ Role: r.Role || "", Type: r.Type || "Weekly", Rate: r.Rate ?? "" });
  }
  async function handleSaveRate() {
    if (!rateForm.Role || rateForm.Rate === "") { setRateMsg("Role and rate are required."); return; }
    setRateBusy(true); setRateMsg("");
    const fields = { Title: rateForm.Role, Role: rateForm.Role, Type: rateForm.Type, Rate: Number(rateForm.Rate) };
    try {
      if (rateEditId) await editRate(rateEditId, fields); else await addRate(fields);
      setRateMsg(`✓ Saved ${rateForm.Role}.`); setRateForm(EMPTY_RATE); setRateEditId(null);
    } catch (e) { setRateMsg("✗ " + e.message); } finally { setRateBusy(false); }
  }
  async function handleDeleteRate(r) {
    if (!window.confirm(`Delete the rate for ${r.Role}?`)) return;
    setRateBusy(true);
    try { await removeRate(r.Id); } catch (e) { alert(e.message); } finally { setRateBusy(false); }
  }
  async function handleLoadDefaults() {
    if (!window.confirm("Load the 2026 billable rates? Roles already in the list will be updated to these values.")) return;
    setRateBusy(true); setRateMsg("Loading 2026 rates…");
    const nm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    try {
      const token = await getToken();
      let n = 0;
      for (const d of DEFAULT_RATES_2026) {
        const existing = billableRates.find(r => nm(r.Role) === nm(d.Role));
        const fields = { Title: d.Role, Role: d.Role, Type: d.Type, Rate: d.Rate };
        if (existing) await sp.updateRate(token, existing.Id, fields);
        else await sp.createRate(token, fields);
        setRateMsg(`Loading… ${++n}/${DEFAULT_RATES_2026.length}`);
      }
      await reload();
      setRateMsg(`✓ Loaded ${DEFAULT_RATES_2026.length} rates.`);
    } catch (e) { setRateMsg("✗ " + e.message); } finally { setRateBusy(false); }
  }

  // add person
  const [form, setForm] = useState(EMPTY_PERSON);
  const [success, setSuccess] = useState(false);
  const handleChange = (key, val) => setForm(f => ({ ...f, [key]: val }));

  // teams
  const [teamProject, setTeamProject] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [poolFilter, setPoolFilter] = useState("All");
  const [openSeatRole, setOpenSeatRole] = useState("PM");
  const [supportRole, setSupportRole] = useState("PX");
  const [supportPerson, setSupportPerson] = useState("");
  const [teamMsg, setTeamMsg] = useState("");
  const [working, setWorking] = useState(false);
  // inline editing of an existing roster member
  const [editingMemberId, setEditingMemberId] = useState(null);
  const [memberEdit, setMemberEdit] = useState({ ProjectRole: "PM", StartDate: "", EndDate: "", AllocationPercent: "100" });
  function startEditMember(m) {
    setEditingMemberId(m.Id);
    setMemberEdit({
      ProjectRole: m.ProjectRole || "PM",
      StartDate: dateOnly(m.StartDate),
      EndDate: dateOnly(m.EndDate),
      AllocationPercent: (m.AllocationPercent != null && m.AllocationPercent !== "") ? String(m.AllocationPercent) : "100",
    });
  }
  async function saveEditMember() {
    setWorking(true); setTeamMsg("");
    try {
      await editTeamMember(editingMemberId, {
        ProjectRole: memberEdit.ProjectRole,
        StartDate: memberEdit.StartDate ? new Date(memberEdit.StartDate).toISOString() : null,
        EndDate: memberEdit.EndDate ? new Date(memberEdit.EndDate).toISOString() : null,
        AllocationPercent: memberEdit.AllocationPercent ? Number(memberEdit.AllocationPercent) : 100,
      });
      setEditingMemberId(null); setTeamMsg("✓ Updated.");
    } catch (e) { setTeamMsg("✗ " + e.message); } finally { setWorking(false); }
  }

  // projects
  const [projForm, setProjForm] = useState(EMPTY_PROJECT);
  const [projEditId, setProjEditId] = useState(null);
  const [projMsg, setProjMsg] = useState("");
  const [projBusy, setProjBusy] = useState(false);
  const [moveCtx, setMoveCtx] = useState(null); // job whose dates moved, awaiting the crew decision
  const [deletedProjects, setDeletedProjects] = useState([]);
  const loadDeleted = async () => {
    // Finance-only endpoint; a 403 just means this user sees an empty bin.
    try { setDeletedProjects(await getDeletedProjects()); } catch (e) { setDeletedProjects([]); }
  };
  useEffect(() => { if (tab === "projects") loadDeleted(); }, [tab]);
  const handleProj = (key, val) => setProjForm(f => ({ ...f, [key]: val }));

  // people mgmt
  const [peopleSearch, setPeopleSearch] = useState("");
  const [rowBusy, setRowBusy] = useState("");
  const isCodeAdmin = (p) => ADMIN_EMAILS.some(e => e.toLowerCase() === (p.Email || "").toLowerCase());
  const isCodeFinance = (p) => FINANCE_EMAILS.some(e => e.toLowerCase() === (p.Email || "").toLowerCase());

  async function handleSavePerson() {
    try {
      await addPerson({
        Title: form.FullName, FullName: form.FullName, Email: form.Email || "",
        JobTitle: form.JobTitle || "", CompanyRole: form.CompanyRole || "", Archetype: form.Archetype || "",
        Survey_A: num(form.Survey_A), Survey_B: num(form.Survey_B), Survey_C: num(form.Survey_C), Survey_D: num(form.Survey_D),
        Survey_EU: num(form.Survey_EU), Survey_L: num(form.Survey_L), Survey_I: num(form.Survey_I),
        Job_A: num(form.Job_A), Job_B: num(form.Job_B), Job_C: num(form.Job_C), Job_D: num(form.Job_D),
        Job_EU: num(form.Job_EU), Job_L: num(form.Job_L), Job_I: num(form.Job_I),
        IsActive: true,
      });
      setSuccess(true); setForm(EMPTY_PERSON); setTimeout(() => setSuccess(false), 3000);
    } catch (e) { alert("Save failed: " + e.message); }
  }
  const personValid = form.FullName && form.Archetype && form.Survey_A !== "" && form.Survey_B !== "" && form.Survey_C !== "" && form.Survey_D !== "";

  // ── teams ──
  function pickProject(t) { setTeamProject(t); }
  // Drag-and-drop / click add. Role, dates & utilization auto-fill from the
  // person and the project; everything is editable on the row afterward.
  async function addPersonToTeam(personName, role) {
    if (!teamProject || !personName) return;
    if (teamMembers.some(m => m.ProjectJobNumber === teamProject && m.PersonName === personName)) {
      setTeamMsg(`${personName} is already on this team.`); return;
    }
    const pr = projects.find(p => p.Title === teamProject);
    setWorking(true); setTeamMsg("");
    try {
      await addTeamMember({
        Title: teamProject, ProjectJobNumber: teamProject, PersonName: personName,
        ProjectRole: role || "PM",
        StartDate: pr?.StartDate || null,
        EndDate: pr?.CompletionDate || null,
        AllocationPercent: 100,
      });
      setTeamMsg(`✓ Added ${personName} to ${teamProject}.`);
    } catch (e) { setTeamMsg("✗ " + (e.message || "Couldn't add.")); } finally { setWorking(false); }
  }
  // Add an OPEN seat (a needed role with no person yet).
  async function addOpenSeat(role) {
    if (!teamProject) return;
    const pr = projects.find(p => p.Title === teamProject);
    setWorking(true); setTeamMsg("");
    try {
      await addTeamMember({
        Title: teamProject, ProjectJobNumber: teamProject, PersonName: OPEN_SEAT,
        ProjectRole: role || "PM", AllocationPercent: 100,
        StartDate: pr?.StartDate || null, EndDate: pr?.CompletionDate || null,
      });
      setTeamMsg(`✓ Added open ${role} seat to ${teamProject}.`);
    } catch (e) { setTeamMsg("✗ " + (e.message || "Couldn't add seat.")); } finally { setWorking(false); }
  }
  // Fill an open seat with a real (or prospective) person.
  async function fillSeat(m, personName) {
    if (!personName) return;
    setWorking(true); setTeamMsg("");
    try { await editTeamMember(m.Id, { PersonName: personName }); setTeamMsg(`✓ Filled the ${m.ProjectRole} seat with ${personName}.`); }
    catch (e) { setTeamMsg("✗ " + (e.message || "Couldn't fill seat.")); } finally { setWorking(false); }
  }
  async function handleRemoveMember(id, name) {
    setWorking(true); setTeamMsg("");
    try { await removeTeamMember(id); setTeamMsg(`Removed ${name}.`); }
    catch (e) { setTeamMsg("✗ " + e.message); } finally { setWorking(false); }
  }

  // ── projects ──
  function loadProject(id) {
    if (!id) { setProjForm(EMPTY_PROJECT); setProjEditId(null); return; }
    const p = projects.find(x => String(x.Id) === String(id));
    if (!p) return;
    setProjEditId(p.Id);
    setProjForm({
      Title: p.Title || "", JobName: p.JobName || "", Address: p.Address || "",
      ProjectExecutive: p.ProjectExecutive || "", ProjectAccountant: p.ProjectAccountant || "",
      Status: p.Status || "Active", BuildingType: p.BuildingType || "Commercial",
      Confidence: p.Confidence != null && p.Confidence !== "" ? Number(p.Confidence) : 50,
      StartDate: dateOnly(p.StartDate), CompletionDate: dateOnly(p.CompletionDate),
      GCBudget: p.GCBudget ?? "", GCRemaining: p.GCRemaining ?? "",
      ProcoreProjectId: p.ProcoreProjectId ?? "",
    });
  }
  // Saves the job, then any team-member date changes the move wizard returned.
  async function persistProject(title, { StartDate, CompletionDate, memberUpdates = [] }) {
    setProjBusy(true); setProjMsg("");
    const fields = {
      Title: title, JobNumber: title, JobName: projForm.JobName, Address: projForm.Address,
      ProjectExecutive: projForm.ProjectExecutive, ProjectAccountant: projForm.ProjectAccountant,
      StartDate: StartDate ? new Date(StartDate).toISOString() : null,
      CompletionDate: CompletionDate ? new Date(CompletionDate).toISOString() : null,
      Status: projForm.Status, BuildingType: projForm.BuildingType,
      Confidence: projForm.Status === "Potential" ? num(projForm.Confidence) : null,
      GCBudget: num(projForm.GCBudget), GCRemaining: num(projForm.GCRemaining),
      ProcoreProjectId: (projForm.ProcoreProjectId || "").trim() || null,
    };
    try {
      if (projEditId) await moveJobAndCrew(projEditId, fields, memberUpdates);
      else await addProject(fields);
      setProjMsg(`✓ ${projEditId ? "Updated" : "Created"} ${title}.${memberUpdates.length ? ` Moved ${memberUpdates.length} ${memberUpdates.length === 1 ? "person" : "people"}.` : ""}`);
      setProjForm(EMPTY_PROJECT); setProjEditId(null); setMoveCtx(null);
    } catch (e) { setProjMsg("✗ " + e.message); throw e; } finally { setProjBusy(false); }
  }

  async function handleSaveProject() {
    let title = (projForm.Title || "").trim();
    if (!title) {
      if (projForm.Status === "Potential") title = "PIPE-" + Date.now().toString().slice(-6); // temp pipeline ID
      else { setProjMsg("Job number is required for active jobs."); return; }
    }
    // Moving an existing job's dates is a staffing decision — ask what happens
    // to the crew rather than silently leaving them on the old schedule.
    const before = projEditId ? projects.find(x => String(x.Id) === String(projEditId)) : null;
    const datesChanged = before &&
      (dateOnly(before.StartDate) !== projForm.StartDate || dateOnly(before.CompletionDate) !== projForm.CompletionDate);
    if (datesChanged && teamMembers.some(m => m.ProjectJobNumber === before.Title)) {
      setMoveCtx({ project: before, title });
      return;
    }
    persistProject(title, { StartDate: projForm.StartDate, CompletionDate: projForm.CompletionDate }).catch(() => {});
  }
  // ── people mgmt ──
  // ── feedback inbox ──
  const [fb, setFb] = useState([]);
  const [fbLoading, setFbLoading] = useState(false);
  const [fbErr, setFbErr] = useState("");
  const [fbShot, setFbShot] = useState(null);
  const loadFeedback = async () => {
    setFbLoading(true); setFbErr("");
    try { setFb(await sp.getFeedback(await getToken())); }
    catch (e) { setFbErr(e.message || "Couldn't load feedback."); }
    finally { setFbLoading(false); }
  };
  useEffect(() => { if (tab === "feedback") loadFeedback(); }, [tab]);
  const setFbStatus = async (row, Status) => {
    try {
      await sp.updateFeedback(await getToken(), row.Id, { Status });
      setFb(list => list.map(x => x.Id === row.Id ? { ...x, Status } : x));
    } catch (e) { setFbErr(e.message || "Couldn't update."); }
  };

  async function toggleAdmin(p) {
    setRowBusy(p.Id);
    // Bust the schema cache first so an IsAdmin column created in SharePoint after
    // sign-in is picked up (otherwise the write is silently dropped).
    try { await editPerson(p.Id, { IsAdmin: !(p.IsAdmin === true) }); }
    catch (e) { alert("Couldn't update: " + e.message); } finally { setRowBusy(""); }
  }
  async function toggleActive(p) {
    setRowBusy(p.Id);
    try { await editPerson(p.Id, { IsActive: !(p.IsActive !== false) }); }
    catch (e) { alert("Couldn't update: " + e.message); } finally { setRowBusy(""); }
  }
  async function toggleFinance(p) {
    setRowBusy(p.Id);
    try { await editPerson(p.Id, { IsFinance: !(p.IsFinance === true) }); }
    catch (e) { alert("Couldn't update: " + e.message); } finally { setRowBusy(""); }
  }
  async function handleRemovePerson(p) {
    if (!window.confirm(`Permanently delete ${p.FullName}? This removes them from People entirely.`)) return;
    setRowBusy(p.Id);
    try { await removePerson(p.Id); }
    catch (e) { alert("Couldn't delete: " + e.message); } finally { setRowBusy(""); }
  }
  async function handleChangeRole(p, role) {
    setRowBusy(p.Id);
    try { await editPerson(p.Id, { CompanyRole: role }); }
    catch (e) { alert("Couldn't update role: " + e.message); } finally { setRowBusy(""); }
  }

  // ── edit a person's CI scores ──
  const SCORE_KEYS = ["Survey_A", "Survey_B", "Survey_C", "Survey_D", "Survey_EU", "Survey_L", "Survey_I", "Job_A", "Job_B", "Job_C", "Job_D", "Job_EU", "Job_L", "Job_I"];
  const [editScoresId, setEditScoresId] = useState(null);
  const [scoreForm, setScoreForm] = useState({});
  const [scoreMsg, setScoreMsg] = useState("");
  const setSc = (k, v) => setScoreForm((f) => ({ ...f, [k]: v }));
  function startEditScores(p) {
    setScoreMsg("");
    const f = { Archetype: p.Archetype || "" };
    SCORE_KEYS.forEach((k) => { f[k] = (p[k] != null && p[k] !== "") ? String(p[k]) : ""; });
    setScoreForm(f);
    setEditScoresId(p.Id);
  }
  async function saveScores() {
    const trait10 = ["Survey_A", "Survey_B", "Survey_C", "Survey_D", "Survey_L", "Survey_I", "Job_A", "Job_B", "Job_C", "Job_D", "Job_L", "Job_I"];
    for (const k of trait10) { const v = scoreForm[k]; if (v !== "" && (isNaN(Number(v)) || Number(v) < 0 || Number(v) > 10)) { setScoreMsg("Traits, Logic & Ingenuity must be 0–10."); return; } }
    for (const k of ["Survey_EU", "Job_EU"]) { const v = scoreForm[k]; if (v !== "" && (isNaN(Number(v)) || Number(v) < 0 || Number(v) > 100)) { setScoreMsg("Energy (EU) must be 0–100."); return; } }
    setRowBusy(editScoresId); setScoreMsg("");
    try {
      const fields = { Archetype: scoreForm.Archetype || "" };
      SCORE_KEYS.forEach((k) => { fields[k] = scoreForm[k] === "" ? null : Number(scoreForm[k]); });
      await editPerson(editScoresId, fields);
      setEditScoresId(null);
    } catch (e) { setScoreMsg("✗ " + e.message); } finally { setRowBusy(""); }
  }

  const currentMembers = teamMembers.filter(m => m.ProjectJobNumber === teamProject);
  // Manage Teams dropdown: completed jobs are done staffing — keep them out so
  // the list stays short. Sorted by job number.
  const manageableProjects = [...projects]
    .filter(p => (p.Status || "Active") !== "Completed")
    .sort((a, b) => (a.Title || "").localeCompare(b.Title || ""));
  // people not already on THIS project (a person can still be on other projects)
  const availablePeople = [...people]
    .filter(p => !currentMembers.some(m => m.PersonName === p.FullName))
    .sort((a, b) => a.FullName.localeCompare(b.FullName));
  const statusColor = (s) => s === "Active" ? "var(--green)" : s === "Potential" ? "var(--gold-text)" : s === "Completed" ? "var(--text-muted)" : "var(--blue)";

  // PX / Accountant dropdown options drawn from the people roster (avoids typos
  // that would fragment a person across spellings). Falls back to all people if
  // none are classified, and always includes the project's current value.
  const uniqSorted = (arr) => [...new Set(arr.filter(Boolean))].sort((a, b) => a.localeCompare(b));
  const byName = [...people].sort((a, b) => a.FullName.localeCompare(b.FullName)).map(p => p.FullName);
  let pxNames = people.filter(p => roleFamily(p.CompanyRole) === "px").map(p => p.FullName);
  if (pxNames.length === 0) pxNames = byName;
  let acctNames = people.filter(p => /account/i.test(p.CompanyRole || "")).map(p => p.FullName);
  if (acctNames.length === 0) acctNames = byName;
  const pxOptions = uniqSorted([...pxNames, projForm.ProjectExecutive]);
  const acctOptions = uniqSorted([...acctNames, projForm.ProjectAccountant]);

  // Roster pool for drag-and-drop staffing, filterable by exact position.
  const presentBuckets = POSITION_ORDER.filter(b => availablePeople.some(p => positionBucket(p.CompanyRole) === b));
  const filteredPool = poolFilter === "All" ? availablePeople : availablePeople.filter(p => positionBucket(p.CompanyRole) === poolFilter);

  // Support = Project Executive / Project Accountant (carried at a utilization %).
  const isSupportRole = (role) => { const f = roleFamily(role); return f === "px" || f === "accountant"; };
  const teamGrp = currentMembers.filter(m => !isSupportRole(m.ProjectRole));
  const supportGrp = currentMembers.filter(m => isSupportRole(m.ProjectRole));

  // Projects list grouped by status for the Admin "All Projects" panel.
  const STATUS_ORDER = ["Active", "Potential", "On Hold", "Completed"];
  const projectsByStatus = (s) => projects
    .filter(p => (p.Status || "Active") === s)
    .sort((a, b) => (a.Title || "").localeCompare(b.Title || ""));
  const otherStatusProjects = projects.filter(p => !STATUS_ORDER.includes(p.Status || "Active"));

  const renderProjectRow = (p, i, list) => (
    <div key={p.Id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 0", borderBottom: i < list.length - 1 ? "1px solid var(--border)" : "none" }}>
      <div>
        <div style={{ fontWeight: 700, fontSize: "14px" }}>{p.Title} — {p.JobName}</div>
        <div style={{ fontSize: "11px", color: "var(--text-muted)" }}>{dateOnly(p.StartDate) || "no start"} → {dateOnly(p.CompletionDate) || "no end"}</div>
      </div>
      <div className="flex gap-8" style={{ alignItems: "center" }}>
        <span style={{ fontSize: "11px", fontWeight: 700, color: statusColor(p.Status), textTransform: "uppercase" }}>{p.Status || "Active"}</span>
        <button className="btn btn-ghost" style={{ padding: "4px 12px", fontSize: "12px" }} onClick={() => loadProject(p.Id)}>Edit</button>
        <button className="btn btn-ghost" style={{ padding: "4px 12px", fontSize: "12px", color: "var(--red)" }}
          onClick={async () => {
            // Soft delete: the job's crew, runoff, narratives and budget
            // snapshots stay in the database and come back with a restore.
            const members = teamMembers.filter(m => m.ProjectJobNumber === p.Title);
            const kept = members.length ? `\n\n${members.length} team assignment(s) will be hidden with it, not erased.` : "";
            if (!window.confirm(`Delete "${p.Title} \u2014 ${p.JobName}"?${kept}\n\nYou can restore it from Recently deleted below.`)) return;
            setProjMsg("");
            try {
              await removeProject(p.Id);
              if (projEditId === p.Id) loadProject("");
              setProjMsg(`Deleted ${p.Title}. Restore it below if that was a mistake.`);
              loadDeleted();
            } catch (e) { setProjMsg("\u2717 " + (e.message || "Couldn't delete project.")); }
          }}>Delete</button>
      </div>
    </div>
  );

  return (
    <div className="page">
      <Link to="/" className="back-btn">← Back to Projects</Link>
      <div className="page-header">
        <div>
          <div className="page-title">Admin Console</div>
          <div className="page-subtitle">Manage people, projects, and teams</div>
        </div>
      </div>

      <div className="admin-tabs">
        {ADMIN_TABS.map(t => (
          <button key={t.id} className={`admin-tab ${tab === t.id ? "active" : ""}`} onClick={() => setTab(t.id)}>
            <Icon name={t.icon} size={18} />
            <span>
              <span className="admin-tab-label" style={{ display: "block" }}>{t.label}</span>
              <span className="admin-tab-desc" style={{ display: "block" }}>{t.desc}</span>
            </span>
          </button>
        ))}
      </div>

      {success && (
        <div style={{ position: "fixed", top: "80px", right: "24px", background: "var(--green)", color: "white", padding: "12px 20px", borderRadius: "var(--radius)", fontSize: "14px", fontWeight: 600, zIndex: 200 }}>✓ Saved</div>
      )}

      {/* ── TEAMS ── */}
      {tab === "teams" && (
        <>
          <div className="form-group" style={{ maxWidth: 420 }}>
            <label className="form-label">Project</label>
            <select className="form-select" value={teamProject} onChange={e => { pickProject(e.target.value); setEditingMemberId(null); }}>
              <option value="">Select a project…</option>
              {manageableProjects.map(p => <option key={p.Id} value={p.Title}>{p.Title} — {p.JobName}{p.Status === "Potential" ? " (pipeline)" : ""}{p.Status === "On Hold" ? " (on hold)" : ""}</option>)}
            </select>
          </div>
          {teamMsg && <div style={{ marginBottom: 12, fontSize: 13, fontWeight: 600, color: teamMsg.startsWith("✗") ? "var(--red)" : "var(--text-primary)" }}>{teamMsg}</div>}
          {!teamProject ? (
            <div className="empty"><div className="empty-text">Pick a project to see and edit its team.</div></div>
          ) : (
            <div className="grid-2-resp" style={{ alignItems: "start" }}>
              {/* Current roster — editable + drop target */}
              <div className="card"
                onDragOver={e => { e.preventDefault(); if (!dragOver) setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => { e.preventDefault(); const name = e.dataTransfer.getData("text/plain"); const per = people.find(p => p.FullName === name); addPersonToTeam(name, per?.CompanyRole); setDragOver(false); }}
                style={dragOver ? { outline: "2px dashed var(--gold)", outlineOffset: 3, background: "var(--gold-soft)" } : undefined}>
                <div className="card-header">
                  <div className="card-title">Current Team — {teamProject}</div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{currentMembers.filter(x => !isOpenSeat(x)).length} staffed · {currentMembers.filter(isOpenSeat).length} open</div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, paddingBottom: 10, borderBottom: "1px solid var(--border)" }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px" }}>Add open seat</span>
                  <select className="form-select" style={{ maxWidth: 150, padding: "4px 8px" }} value={openSeatRole} onChange={e => setOpenSeatRole(e.target.value)}>
                    {PROJECT_ROLES.map(r => <option key={r}>{r}</option>)}
                  </select>
                  <button className="btn btn-ghost" style={{ fontSize: 12, padding: "4px 12px" }} onClick={() => addOpenSeat(openSeatRole)} disabled={working}>+ Add seat</button>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 10, paddingBottom: 10, borderBottom: "1px solid var(--border)" }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px" }}>Add support</span>
                  <select className="form-select" style={{ maxWidth: 150, padding: "4px 8px" }} value={supportRole} onChange={e => { setSupportRole(e.target.value); setSupportPerson(""); }}>
                    <option>PX</option>
                    <option>Project Accountant</option>
                  </select>
                  <select className="form-select" style={{ maxWidth: 200, padding: "4px 8px" }} value={supportPerson} onChange={e => setSupportPerson(e.target.value)}>
                    <option value="">Select person…</option>
                    {(() => {
                      const fam = roleFamily(supportRole);
                      let opts = availablePeople.filter(p => roleFamily(p.CompanyRole) === fam);
                      if (opts.length === 0) opts = availablePeople;
                      return opts.map(p => <option key={p.Id} value={p.FullName}>{p.FullName} · {p.CompanyRole}</option>);
                    })()}
                  </select>
                  <button className="btn btn-ghost" style={{ fontSize: 12, padding: "4px 12px" }} disabled={working || !supportPerson} onClick={() => { addPersonToTeam(supportPerson, supportRole); setSupportPerson(""); }}>+ Add support</button>
                </div>
                {currentMembers.length === 0 ? (
                  <div className="empty"><div className="empty-text">{dragOver ? "Drop to add to this team" : "No seats yet. Add an open seat above, or drag a person from the right →"}</div></div>
                ) : [...teamGrp, ...supportGrp].map((m, i, arr) => (
                  <React.Fragment key={m.Id}>
                  {i === 0 && teamGrp.length > 0 && <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".5px", color: "var(--text-muted)", padding: "4px 0" }}>Team · {teamGrp.length}</div>}
                  {i === teamGrp.length && <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".5px", color: "var(--blue)", padding: "10px 0 4px", borderTop: teamGrp.length ? "1px solid var(--border)" : "none" }}>Support · PX &amp; Project Accountant</div>}
                  <div style={{ padding: "10px 0", borderBottom: i < arr.length - 1 ? "1px solid var(--border)" : "none" }}>
                    {isOpenSeat(m) ? (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                          <span style={{ fontSize: 10, fontWeight: 800, color: "var(--gold-text)", background: "var(--gold-soft)", border: "1px dashed var(--gold)", borderRadius: 6, padding: "3px 8px", whiteSpace: "nowrap" }}>OPEN SEAT</span>
                          <span style={{ fontWeight: 700, fontSize: 14 }}>{m.ProjectRole}</span>
                        </div>
                        <div className="flex gap-8" style={{ alignItems: "center" }}>
                          <select className="form-select" style={{ maxWidth: 200, padding: "4px 8px" }} value="" onChange={e => { if (e.target.value) fillSeat(m, e.target.value); }} disabled={working}>
                            <option value="">Fill with…</option>
                            {availablePeople.filter(p => roleFamily(p.CompanyRole) === roleFamily(m.ProjectRole)).map(p => <option key={p.Id} value={p.FullName}>{p.FullName} · {p.CompanyRole}</option>)}
                          </select>
                          <button className="btn btn-ghost" style={{ padding: "4px 12px", fontSize: 12, color: "var(--red)" }} onClick={() => handleRemoveMember(m.Id, "open seat")} disabled={working}>Remove</button>
                        </div>
                      </div>
                    ) : editingMemberId === m.Id ? (
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>{m.PersonName}</div>
                        <div className="grid-2">
                          <div className="form-group">
                            <label className="form-label">Role</label>
                            <select className="form-select" value={memberEdit.ProjectRole} onChange={e => setMemberEdit(f => ({ ...f, ProjectRole: e.target.value }))}>
                              {PROJECT_ROLES.map(r => <option key={r}>{r}</option>)}
                            </select>
                          </div>
                          <div className="form-group">
                            <label className="form-label">Utilization %</label>
                            <input type="number" min="0" max="100" className="form-input" value={memberEdit.AllocationPercent} onChange={e => setMemberEdit(f => ({ ...f, AllocationPercent: e.target.value }))} />
                          </div>
                        </div>
                        <div className="grid-2">
                          <div className="form-group"><label className="form-label">Start</label><input type="date" className="form-input" value={memberEdit.StartDate} onChange={e => setMemberEdit(f => ({ ...f, StartDate: e.target.value }))} /></div>
                          <div className="form-group"><label className="form-label">End</label><input type="date" className="form-input" value={memberEdit.EndDate} onChange={e => setMemberEdit(f => ({ ...f, EndDate: e.target.value }))} /></div>
                        </div>
                        <div className="flex gap-8">
                          <button className="btn btn-primary" style={{ padding: "5px 14px", fontSize: 12 }} onClick={saveEditMember} disabled={working}>Save</button>
                          <button className="btn btn-ghost" style={{ padding: "5px 14px", fontSize: 12 }} onClick={() => setEditingMemberId(null)}>Cancel</button>
                        </div>
                        <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 6 }}>Rate follows the role (set in the Rates tab).</div>
                      </div>
                    ) : (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 14 }}>{m.PersonName}</div>
                          <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                            {m.ProjectRole}{m.AllocationPercent != null ? ` · ${m.AllocationPercent}%` : ""}
                            {m.StartDate ? ` · ${dateOnly(m.StartDate)} → ${dateOnly(m.EndDate) || "?"}` : ""}
                          </div>
                        </div>
                        <div className="flex gap-8">
                          <button className="btn btn-ghost" style={{ padding: "4px 12px", fontSize: 12 }} onClick={() => startEditMember(m)} disabled={working}>Edit</button>
                          <button className="btn btn-ghost" style={{ padding: "4px 12px", fontSize: 12, color: "var(--red)" }} onClick={() => handleRemoveMember(m.Id, m.PersonName)} disabled={working}>Remove</button>
                        </div>
                      </div>
                    )}
                  </div>
                  </React.Fragment>
                ))}
              </div>

              {/* Available roster — drag onto the team (or click to add) */}
              <div className="card" style={{ borderLeft: "3px solid var(--gold)" }}>
                <div className="card-header">
                  <div className="card-title">Available People</div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{filteredPool.length}</div>
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
                  {["All", ...presentBuckets].map(b => (
                    <div key={b} className={`filter-chip ${poolFilter === b ? "active" : ""}`} style={{ fontSize: 11 }} onClick={() => setPoolFilter(b)}>{b}</div>
                  ))}
                </div>
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 10 }}>Drag a person onto the team — or click to add. Role, dates &amp; utilization auto-fill and can be edited on their row.</div>
                {availablePeople.length === 0 ? (
                  <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Everyone is already on this project.</div>
                ) : filteredPool.length === 0 ? (
                  <div style={{ fontSize: 12, color: "var(--text-muted)" }}>No available people in this group.</div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 460, overflowY: "auto" }}>
                    {filteredPool.map(p => (
                      <div key={p.Id}
                        draggable
                        onDragStart={e => e.dataTransfer.setData("text/plain", p.FullName)}
                        onClick={() => addPersonToTeam(p.FullName, p.CompanyRole)}
                        title="Drag onto the team, or click to add"
                        style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--bg-card)", cursor: "grab" }}>
                        <span className="avatar" style={{ width: 28, height: 28, fontSize: 10, background: roleColor(p.CompanyRole) }}>{(p.FullName || "").split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}</span>
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ fontWeight: 700, fontSize: 13 }}>{p.FullName}</span>
                          <span style={{ color: "var(--text-muted)", fontSize: 11, marginLeft: 6 }}>{p.CompanyRole}</span>
                        </span>
                        <span style={{ color: "var(--gold-text)", fontSize: 16, fontWeight: 800 }}>+</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* ── PROJECTS ── */}
      {tab === "projects" && (
        <div className="grid-2-resp" style={{ alignItems: "start" }}>
          <div className="card">
            <div className="card-header"><div className="card-title">{projEditId ? "Edit Project" : "New Project"}</div></div>
            <div className="form-group">
              <label className="form-label">Editing</label>
              <select className="form-select" value={projEditId || ""} onChange={e => loadProject(e.target.value)}>
                <option value="">➕ Create new project</option>
                {STATUS_ORDER.filter(s => projectsByStatus(s).length > 0).map(s => (
                  <optgroup key={s} label={s}>
                    {projectsByStatus(s).map(p => <option key={p.Id} value={p.Id}>{p.Title} — {p.JobName}</option>)}
                  </optgroup>
                ))}
                {otherStatusProjects.length > 0 && (
                  <optgroup label="Other">
                    {otherStatusProjects.map(p => <option key={p.Id} value={p.Id}>{p.Title} — {p.JobName}</option>)}
                  </optgroup>
                )}
              </select>
            </div>
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Job Number {projForm.Status === "Potential" ? "(optional)" : "*"}</label>
                <input className="form-input" value={projForm.Title} onChange={e => handleProj("Title", e.target.value)} placeholder={projForm.Status === "Potential" ? "auto-assigned if left blank" : "3045-026"} />
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-select" value={projForm.Status} onChange={e => handleProj("Status", e.target.value)}>
                  {PROJECT_STATUSES.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Job Name</label>
                <input className="form-input" value={projForm.JobName} onChange={e => handleProj("JobName", e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Building Type</label>
                <select className="form-select" value={projForm.BuildingType} onChange={e => handleProj("BuildingType", e.target.value)}>
                  {BUILDING_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            {projForm.Status === "Potential" && (
              <div className="form-group">
                <label className="form-label">Win Confidence: {projForm.Confidence}%</label>
                <input type="range" min="5" max="100" step="5" value={projForm.Confidence} onChange={e => handleProj("Confidence", parseInt(e.target.value))} style={{ width: "100%", accentColor: "var(--gold)" }} />
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Address</label>
              <input className="form-input" value={projForm.Address} onChange={e => handleProj("Address", e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Procore Project ID <span style={{ fontWeight: 400, color: "var(--text-muted)" }}>— enables live budget pulls</span></label>
              <input className="form-input" value={projForm.ProcoreProjectId} onChange={e => handleProj("ProcoreProjectId", e.target.value)} placeholder="e.g. 3367963" />
            </div>
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Project Executive</label>
                <select className="form-select" value={projForm.ProjectExecutive} onChange={e => handleProj("ProjectExecutive", e.target.value)}>
                  <option value="">Select…</option>
                  {pxOptions.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Project Accountant</label>
                <select className="form-select" value={projForm.ProjectAccountant} onChange={e => handleProj("ProjectAccountant", e.target.value)}>
                  <option value="">Select…</option>
                  {acctOptions.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            </div>
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Start Date</label>
                <input type="date" className="form-input" value={projForm.StartDate} onChange={e => handleProj("StartDate", e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">End Date</label>
                <input type="date" className="form-input" value={projForm.CompletionDate} onChange={e => handleProj("CompletionDate", e.target.value)} />
              </div>
            </div>
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Starting GC ($)</label>
                <input type="number" className="form-input" value={projForm.GCBudget} onChange={e => handleProj("GCBudget", e.target.value)} placeholder="e.g. 850000" />
              </div>
              <div className="form-group">
                <label className="form-label">Current GC remaining ($)</label>
                <input type="number" className="form-input" value={projForm.GCRemaining} onChange={e => handleProj("GCRemaining", e.target.value)} placeholder="optional" />
              </div>
            </div>
            <button className="btn btn-primary" style={{ width: "100%" }} onClick={handleSaveProject} disabled={projBusy || (!projForm.Title && projForm.Status !== "Potential")}>
              {projBusy ? "Saving…" : projEditId ? "Save Changes" : "Create Project"}
            </button>
            {projEditId && <button className="btn btn-ghost" style={{ width: "100%", marginTop: "8px" }} onClick={() => loadProject("")}>Cancel / New</button>}
            {projMsg && <div style={{ marginTop: "12px", fontSize: "13px", fontWeight: 600, color: projMsg.startsWith("✗") ? "var(--red)" : "var(--text-primary)" }}>{projMsg}</div>}

            {moveCtx && (
              <MoveJobWizard
                project={moveCtx.project}
                members={teamMembers.filter(m => m.ProjectJobNumber === moveCtx.project.Title)}
                initialStart={projForm.StartDate}
                initialEnd={projForm.CompletionDate}
                startStep="crew"
                onCancel={() => setMoveCtx(null)}
                onApply={async ({ StartDate, EndDate, memberUpdates }) => {
                  setProjForm(f => ({ ...f, StartDate: StartDate || "", CompletionDate: EndDate || "" }));
                  await persistProject(moveCtx.title, { StartDate, CompletionDate: EndDate, memberUpdates });
                }}
              />
            )}
          </div>

          <div className="card">
            <div className="card-header"><div className="card-title">All Projects</div><div style={{ fontSize: "12px", color: "var(--text-muted)" }}>{projects.length}</div></div>
            {STATUS_ORDER.map(status => {
              const grp = projectsByStatus(status);
              if (grp.length === 0) return null;
              return (
                <div key={status} style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".5px", color: statusColor(status), padding: "8px 0 6px", borderBottom: "2px solid var(--border)" }}>{status} · {grp.length}</div>
                  {grp.map((p, i) => renderProjectRow(p, i, grp))}
                </div>
              );
            })}
            {otherStatusProjects.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".5px", color: "var(--text-muted)", padding: "8px 0 6px", borderBottom: "2px solid var(--border)" }}>Other · {otherStatusProjects.length}</div>
                {otherStatusProjects.map((p, i) => renderProjectRow(p, i, otherStatusProjects))}
              </div>
            )}

            {deletedProjects.length > 0 && (
              <div style={{ marginTop: 18, paddingTop: 14, borderTop: "2px solid var(--border)" }}>
                <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".5px", color: "var(--text-muted)", paddingBottom: 6 }}>
                  Recently deleted · {deletedProjects.length}
                </div>
                <div style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 10 }}>
                  Nothing here is erased. Restoring a job brings back its crew, runoff, narratives and budget snapshots exactly as they were.
                </div>
                {deletedProjects.map((p, i) => (
                  <div key={p.Id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderBottom: i < deletedProjects.length - 1 ? "1px solid var(--border)" : "none" }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: "var(--text-secondary)" }}>{p.Title} — {p.JobName}</div>
                      <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                        deleted {p.DeletedAt ? String(p.DeletedAt).slice(0, 10) : "—"}{p.DeletedBy ? ` by ${p.DeletedBy}` : ""}
                      </div>
                    </div>
                    <button className="btn btn-ghost" style={{ padding: "4px 12px", fontSize: 12 }}
                      onClick={async () => {
                        setProjMsg("");
                        try {
                          await restoreProject(p.Id);
                          setProjMsg(`✓ Restored ${p.Title}.`);
                          loadDeleted();
                        } catch (e) { setProjMsg("✗ " + (e.message || "Couldn't restore project.")); }
                      }}>Restore</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── RATES ── */}
      {tab === "rates" && (
        <div className="grid-2-resp" style={{ alignItems: "start" }}>
          <div className="card">
            <div className="card-header"><div className="card-title">{rateEditId ? "Edit Rate" : "Add Rate"}</div></div>
            <div className="form-group">
              <label className="form-label">Role *</label>
              <select className="form-select" value={rateForm.Role} onChange={e => handleRate("Role", e.target.value)}>
                <option value="">Select a role…</option>
                {RATE_ROLES.map(r => <option key={r}>{r}</option>)}
              </select>
            </div>
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Type</label>
                <select className="form-select" value={rateForm.Type} onChange={e => handleRate("Type", e.target.value)}>
                  <option>Weekly</option><option>Monthly</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Rate ($)</label>
                <input type="number" className="form-input" value={rateForm.Rate} onChange={e => handleRate("Rate", e.target.value)} placeholder="e.g. 4500" />
              </div>
            </div>
            <button className="btn btn-primary" style={{ width: "100%" }} onClick={handleSaveRate} disabled={rateBusy || !rateForm.Role || rateForm.Rate === ""}>
              {rateBusy ? "Saving…" : rateEditId ? "Save Changes" : "Add Rate"}
            </button>
            {rateEditId && <button className="btn btn-ghost" style={{ width: "100%", marginTop: 8 }} onClick={() => loadRate(null)}>Cancel / New</button>}
            {rateMsg && <div style={{ marginTop: 12, fontSize: 13, fontWeight: 600, color: rateMsg.startsWith("✗") ? "var(--red)" : "var(--text-primary)" }}>{rateMsg}</div>}
            <div style={{ marginTop: 12, fontSize: 11, color: "var(--text-muted)" }}>Weekly rates convert to monthly (× 52 ÷ 12) for the GC burn-down. Update anytime.</div>
            <div className="divider" />
            <button className="btn btn-secondary" onClick={handleLoadDefaults} disabled={rateBusy} style={{ width: "100%" }}>Load 2026 billable rates (one-click)</button>
          </div>
          <div className="card">
            <div className="card-header"><div className="card-title">Billable Rates</div><div style={{ fontSize: 12, color: "var(--text-muted)" }}>{billableRates.length}</div></div>
            {billableRates.length === 0 ? (
              <div className="empty"><div className="empty-text">No rates yet. Add one on the left.</div></div>
            ) : (
              [...billableRates].sort((a, b) => (a.Role || "").localeCompare(b.Role || "")).map((r, i, arr) => (
                <div key={r.Id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 0", borderBottom: i < arr.length - 1 ? "1px solid var(--border)" : "none" }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{r.Role}</div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>${Number(r.Rate || 0).toLocaleString()} / {r.Type === "Monthly" ? "month" : "week"}</div>
                  </div>
                  <div className="flex gap-8">
                    <button className="btn btn-ghost" style={{ padding: "4px 12px", fontSize: 12 }} onClick={() => loadRate(r)}>Edit</button>
                    <button className="btn btn-ghost" style={{ padding: "4px 12px", fontSize: 12, color: "var(--red)" }} onClick={() => handleDeleteRate(r)} disabled={rateBusy}>Delete</button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* ── PEOPLE (manage) ── */}
      {tab === "people" && (
        <div className="card" style={{ maxWidth: "780px" }}>
          <div className="card-header">
            <div className="card-title">Manage People</div>
            <div style={{ fontSize: "12px", color: "var(--text-muted)" }}>Admin access · active status · remove</div>
          </div>
          <input className="form-input" style={{ marginBottom: "16px" }} placeholder="Search people…" value={peopleSearch} onChange={e => setPeopleSearch(e.target.value)} />
          {[...people]
            .filter(p => p.FullName.toLowerCase().includes(peopleSearch.toLowerCase()))
            .sort((a, b) => a.FullName.localeCompare(b.FullName))
            .map((p, i, arr) => {
              const codeAdmin = isCodeAdmin(p);
              const codeFinance = isCodeFinance(p);
              const admin = p.IsAdmin === true || codeAdmin;
              const finance = p.IsFinance === true || codeFinance || admin;
              const active = p.IsActive !== false;
              return (
                <div key={p.Id} style={{ borderBottom: i < arr.length - 1 ? "1px solid var(--border)" : "none", opacity: active ? 1 : 0.5 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", padding: "10px 0" }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: "14px" }}>
                        {p.FullName}
                        {admin && <span style={{ fontSize: "10px", color: "var(--green)", fontWeight: 800, marginLeft: 6 }}>ADMIN</span>}
                        {finance && !admin && <span style={{ fontSize: "10px", color: "var(--blue)", fontWeight: 800, marginLeft: 6 }}>FINANCE</span>}
                        {!active && <span style={{ fontSize: "10px", color: "var(--text-muted)", fontWeight: 800, marginLeft: 6 }}>INACTIVE</span>}
                      </div>
                      <div style={{ fontSize: "12px", color: "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.Email}</div>
                    </div>
                    <div className="flex gap-8" style={{ alignItems: "center", flexShrink: 0 }}>
                      <select className="form-select" style={{ padding: "3px 8px", fontSize: 12, width: "auto" }} value={p.CompanyRole || ""} onChange={e => handleChangeRole(p, e.target.value)} disabled={rowBusy === p.Id} title="Change role (promotion)">
                        {(COMPANY_ROLES.includes(p.CompanyRole) ? COMPANY_ROLES : [p.CompanyRole || "Other", ...COMPANY_ROLES]).map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                      <button className="btn btn-ghost" style={{ padding: "3px 10px", fontSize: "11px" }} onClick={() => (editScoresId === p.Id ? setEditScoresId(null) : startEditScores(p))} disabled={rowBusy === p.Id}>
                        {editScoresId === p.Id ? "Close" : "Scores"}
                      </button>
                      {!codeAdmin && (
                        <button className="btn btn-ghost" style={{ padding: "3px 10px", fontSize: "11px", color: admin ? "var(--text-muted)" : "var(--green)" }} onClick={() => toggleAdmin(p)} disabled={rowBusy === p.Id}>
                          {admin ? "Revoke admin" : "Make admin"}
                        </button>
                      )}
                      {!codeFinance && !admin && (
                        <button className="btn btn-ghost" style={{ padding: "3px 10px", fontSize: "11px", color: p.IsFinance === true ? "var(--text-muted)" : "var(--blue)" }} onClick={() => toggleFinance(p)} disabled={rowBusy === p.Id} title="Finance can edit the Financials tab">
                          {p.IsFinance === true ? "Revoke finance" : "Make finance"}
                        </button>
                      )}
                      <button className="btn btn-ghost" style={{ padding: "3px 10px", fontSize: "11px" }} onClick={() => toggleActive(p)} disabled={rowBusy === p.Id}>
                        {active ? "Deactivate" : "Reactivate"}
                      </button>
                      <button className="btn btn-ghost" style={{ padding: "3px 10px", fontSize: "11px", color: "var(--red)" }} onClick={() => handleRemovePerson(p)} disabled={rowBusy === p.Id}>
                        Remove
                      </button>
                    </div>
                  </div>
                  {editScoresId === p.Id && (
                    <div style={{ padding: "12px 0 16px", borderTop: "1px dashed var(--border)" }}>
                      <div className="form-group" style={{ maxWidth: 280 }}>
                        <label className="form-label">Archetype</label>
                        <select className="form-select" value={scoreForm.Archetype || ""} onChange={e => setSc("Archetype", e.target.value)}>
                          <option value="">Select…</option>
                          {ARCHETYPES.map(a => <option key={a}>{a}</option>)}
                        </select>
                      </div>
                      <div style={{ fontSize: 11, color: "var(--text-muted)", margin: "4px 0 8px" }}>Traits 0–10. Left = Survey (natural), right = On-Job.</div>
                      {["A", "B", "C", "D"].map(t => (
                        <div key={t} className="flex gap-8" style={{ alignItems: "center", marginBottom: 6, maxWidth: 320 }}>
                          <span style={{ width: 16, fontWeight: 800, fontSize: 12 }}>{t}</span>
                          <input type="number" min="0" max="10" className="form-input" style={{ padding: "6px 8px" }} placeholder="Survey" value={scoreForm[`Survey_${t}`] || ""} onChange={e => setSc(`Survey_${t}`, e.target.value)} />
                          <input type="number" min="0" max="10" className="form-input" style={{ padding: "6px 8px" }} placeholder="On-Job" value={scoreForm[`Job_${t}`] || ""} onChange={e => setSc(`Job_${t}`, e.target.value)} />
                        </div>
                      ))}
                      <div className="grid-3" style={{ marginTop: 8, maxWidth: 480 }}>
                        {[["Energy (0–100)", "Survey_EU", "Job_EU"], ["Logic (0–10)", "Survey_L", "Job_L"], ["Ingenuity (0–10)", "Survey_I", "Job_I"]].map(([label, sk, jk]) => (
                          <div key={sk}>
                            <label className="form-label">{label}</label>
                            <div className="flex gap-8">
                              <input type="number" className="form-input" style={{ padding: "6px 8px" }} placeholder="S" value={scoreForm[sk] || ""} onChange={e => setSc(sk, e.target.value)} />
                              <input type="number" className="form-input" style={{ padding: "6px 8px" }} placeholder="J" value={scoreForm[jk] || ""} onChange={e => setSc(jk, e.target.value)} />
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-8" style={{ marginTop: 12, alignItems: "center" }}>
                        <button className="btn btn-primary" style={{ padding: "5px 14px", fontSize: 12 }} onClick={saveScores} disabled={rowBusy === p.Id}>Save Scores</button>
                        <button className="btn btn-ghost" style={{ padding: "5px 14px", fontSize: 12 }} onClick={() => setEditScoresId(null)}>Cancel</button>
                        {scoreMsg && <span style={{ fontSize: 12, color: "var(--red)", fontWeight: 600 }}>{scoreMsg}</span>}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          <div style={{ marginTop: "12px", fontSize: "11px", color: "var(--text-muted)" }}>
            Admin needs an <b>IsAdmin</b> column on People; Finance (can edit the Financials tab) needs <b>IsFinance</b>. Admins are always finance.
            "Deactivate" hides someone from the bench &amp; counts (keeps their record); "Remove" deletes them entirely.
          </div>
        </div>
      )}

      {/* ── FEEDBACK INBOX ── */}
      {tab === "feedback" && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">Feedback</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{fb.filter(f => f.Status === "Open").length} open · {fb.length} total</span>
              <button className="btn btn-ghost" style={{ fontSize: 12, padding: "4px 12px" }} onClick={loadFeedback} disabled={fbLoading}>Refresh</button>
            </div>
          </div>
          {fbErr && <div style={{ color: "var(--red)", fontSize: 13, marginBottom: 8 }}>{fbErr}</div>}
          {fbLoading ? (
            <div className="empty"><div className="spinner" /></div>
          ) : fb.length === 0 ? (
            <div style={{ fontSize: 13, color: "var(--text-muted)" }}>No feedback yet. The “Give Feedback” button is on every page.</div>
          ) : fb.map(row => {
            const catColor = row.Category === "Bug" ? "var(--red)" : row.Category === "Question" ? "var(--blue)" : "var(--gold-text)";
            const done = row.Status === "Done" || row.Status === "Declined";
            return (
              <div key={row.Id} style={{ padding: "12px 0", borderBottom: "1px solid var(--border)", opacity: done ? 0.55 : 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
                  <span style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".5px", color: catColor, border: `1px solid ${catColor}`, borderRadius: 999, padding: "1px 8px" }}>{row.Category}</span>
                  <b style={{ fontSize: 13 }}>{row.SubmitterName || row.SubmittedBy || "Someone"}</b>
                  <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
                    {row.CreatedAt ? new Date(row.CreatedAt).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : ""}
                    {row.PageUrl ? ` · ${row.PageUrl}` : ""}
                  </span>
                  <select className="form-select" style={{ marginLeft: "auto", maxWidth: 130, fontSize: 12, padding: "3px 8px" }}
                    value={row.Status} onChange={e => setFbStatus(row, e.target.value)}>
                    {["Open", "Planned", "Done", "Declined"].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div style={{ fontSize: 13, whiteSpace: "pre-wrap", marginBottom: 6 }}>{row.Note}</div>
                {row.ElementLabel && (
                  <div style={{ fontSize: 11, color: "var(--gold-text)", fontWeight: 700, marginBottom: 6 }}>Pointed at: {row.ElementLabel}</div>
                )}
                {row.Screenshot && (
                  <button className="fin-linklike" style={{ fontSize: 12 }} onClick={() => setFbShot(row.Screenshot)}>View screenshot</button>
                )}
              </div>
            );
          })}
          {fbShot && (
            <div onClick={() => setFbShot(null)}
              style={{ position: "fixed", inset: 0, background: "rgba(16,24,48,.72)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, cursor: "zoom-out" }}>
              <img src={fbShot} alt="feedback screenshot" style={{ maxWidth: "100%", maxHeight: "100%", borderRadius: 8, boxShadow: "0 12px 40px rgba(0,0,0,.4)" }} />
            </div>
          )}
        </div>
      )}

      {/* ── ADD PERSON ── */}
      {tab === "import" && (
        <div className="card" style={{ maxWidth: "560px" }}>
          <div className="card-header"><div className="card-title">Add a Person</div><div style={{ fontSize: "12px", color: "var(--text-muted)" }}>Saves to SharePoint</div></div>
          <div className="form-group">
            <label className="form-label">Full Name *</label>
            <input className="form-input" value={form.FullName} onChange={e => handleChange("FullName", e.target.value)} placeholder="e.g. Mike Towns" />
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-input" type="email" value={form.Email} onChange={e => handleChange("Email", e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Company Role *</label>
              <select className="form-select" value={form.CompanyRole} onChange={e => handleChange("CompanyRole", e.target.value)}>
                {COMPANY_ROLES.map(r => <option key={r}>{r}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Job Title</label>
            <input className="form-input" value={form.JobTitle} onChange={e => handleChange("JobTitle", e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Archetype *</label>
            <select className="form-select" value={form.Archetype} onChange={e => handleChange("Archetype", e.target.value)}>
              <option value="">Select archetype...</option>
              {ARCHETYPES.map(a => <option key={a}>{a}</option>)}
            </select>
          </div>
          <div className="divider" />
          {[
            { label: "A — Autonomy", s: "Survey_A", j: "Job_A" },
            { label: "B — Social Ability", s: "Survey_B", j: "Job_B" },
            { label: "C — Pace", s: "Survey_C", j: "Job_C" },
            { label: "D — Conformity", s: "Survey_D", j: "Job_D" },
          ].map(({ label, s, j }) => (
            <TraitInput key={s} label={label} surveyKey={s} jobKey={j} values={form} onChange={handleChange} />
          ))}
          <div className="divider" />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", marginBottom: "24px" }}>
            {[{ label: "EU", key: "Survey_EU" }, { label: "L", key: "Survey_L" }, { label: "I", key: "Survey_I" }].map(({ label, key }) => (
              <div key={key}>
                <label className="form-label">{label}</label>
                <input type="number" className="form-input" style={{ padding: "8px 10px" }} value={form[key]} onChange={e => handleChange(key, e.target.value)} />
              </div>
            ))}
          </div>
          <button className="btn btn-primary" style={{ width: "100%" }} onClick={handleSavePerson} disabled={!personValid}>Save Profile to SharePoint</button>
        </div>
      )}
    </div>
  );
}
